import React, { useState, useEffect } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { CircularProgress } from "@mui/material";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import { useAuth } from "../../../context/AuthProvider";
import uRLs from "../../../utils/baseUtils";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import InterRegular from "../estimation/font/Inter_24pt-Regular-normal";
import InterBold from "../estimation/font/Inter_24pt-Bold-normal";
import MatchStock from "./MatchStock";
import { useContextApi } from "../../../context/ContextApi";
import { toSecondaryQuantity } from "@heartinz/finance";
import { formatCurrencyFun, formatCurrencyFunWithoutConvert, getDisplayPrices } from "../../../utils/formatCurrencyFunc";
import { usePermission } from "../../../context/PermissionContext";
import BarcodeGenerator from "../product/BarCode";
import { BarcodeIcon, EditIcon, PrinterIcon, TrashIcon, XIcon } from "../../../constants/Icons";

const ViewStockTransfer = ({ data, onClose, onStockUpdate }) => {
  // console.log(
  //   "🚀 ~ file: ViewStockTransfer.js:17 ~ ViewStockTransfer ~ data:",
  //   data,
  // );
  const { isUser } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const [editItem, setEditItem] = useState(null);
  const { useEscapeKey } = useContextApi();
  useEscapeKey(onClose);
  const { permission, hasPermission } = usePermission();
  const [selectedBarcode, setSelectedBarcode] = useState("");
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [isEditBtnLoading, setEditBtnLoading] = useState({});
  const [isDeleteBtnLoading, setDeleteBtnLoading] = useState({});
  const [stockData, setStockData] = useState(data);
  const [isLandingCostBtnLoading, setIsLandingCostBtnLoading] = useState(
    stockData.transferitems.map(() => false),
  );
  const [landingCosts, setLandingCosts] = useState([]);
  const [quantities, setQuantities] = useState(
    stockData.transferitems.reduce((acc, product, index) => {
      acc[index] = product.quantity;
      return acc;
    }, {}),
  );

  const handleQuantityChange = (index, value) => {
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue > 0) {
      setQuantities((prev) => ({
        ...prev,
        [index]: parsedValue,
      }));
    }
  };

  useEffect(() => {
    if (stockData?.transferitems) {
      setLandingCosts(
        stockData.transferitems.map((item) => item.landingcost || 0),
      );
      setQuantities(stockData.transferitems.map((item) => item.quantity || 0));
    }
  }, [stockData]);

  const handleClose = () => {
    onClose();
  };

  const handleStockItems = async (saleId) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stocktransfer/${saleId}`,
      );
      const results = response.data.results;
      return results;
    } catch (error) {
      handleError(error, showAlert);
      return null;
    }
  };

  const openEditModal = async (citem) => {
    // console.log(
    //   "🚀 ~ file: ViewStockTransfer.js:120 ~ openEditModal ~ citem:",
    //   citem,
    // );

    // get full stock transfer data
    const stockData = await handleStockItems(data?._id);

    if (!stockData) return;

    // find selected item from transferitems
    const selectedItem = stockData?.transferitems?.find(
      (item) => item?._id === citem?._id,
    );

    if (!selectedItem) return;

    setEditItem({
      ...selectedItem,
      landingcost: CurrencyConverter.toCurrency(
        selectedItem?.landingcost || 0,
        "INR",
      ),
      cMode: "edit",
    });
  };

  const closeEditModal = () => {
    setEditItem(null);
  };

  const getTextColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return [255, 165, 0]; // Orange
      case "accepted":
        return [0, 128, 0]; // Green
      default:
        return [128, 128, 128]; // Grey
    }
  };

  const hasLandingCost = stockData.transferitems.some(
    (product) => product.landingcost && product.landingcost !== 0,
  );

  const generateTransferPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const padding = 2;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(14);
    doc.setLineDash([1, 1], 0);
    doc.line(padding, padding + 10, pageWidth - padding, padding + 10);
    doc.text("TRANSFER RECEIPT", pageWidth / 2, padding + 18, {
      align: "center",
    });
    doc.line(padding, padding + 22, pageWidth - padding, padding + 22);
    doc.setLineDash([]);

    let currentY = padding + 32;
    doc.setFontSize(10);
    doc.setFont("Inter-Bold", "bold");
    doc.text("No:", padding + 2, currentY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(stockData.transferno || "-", padding + 14, currentY);
    currentY += 6;
    doc.setFont("Inter-Bold", "bold");
    doc.text("Date:", padding + 2, currentY);
    doc.setFont("Inter-Regular", "normal");
    const dateStr = stockData.transferdate
      ? DateConverter.ISOToDisplayFormat(stockData.transferdate).date
      : "-";
    doc.text(dateStr, padding + 14, currentY);
    currentY += 10;

    const fromX = 55;
    const toX = pageWidth - 55;
    const arrowX = pageWidth / 2;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);

    doc.text("From", fromX, currentY, { align: "center" });

    doc.setFontSize(16); // ↑ Increased arrow size
    doc.text("→", arrowX, currentY, { align: "center" });

    doc.setFontSize(12); // ↓ Reset to normal for next text
    doc.text("To", toX, currentY, { align: "center" });

    currentY += 5;
    doc.setFontSize(10);

    doc.text(
      `${stockData.transferfrom?.stype || ""} - ${
        stockData.transferfrom?.title || ""
      }`,
      fromX,
      currentY,
      { align: "center", font: "Inter-Bold", fontStyle: "bold" },
    );
    doc.text(
      `${stockData.transferto?.stype || ""} - ${
        stockData.transferto?.title || ""
      }`,
      toX,
      currentY,
      { align: "center", font: "Inter-Bold", fontStyle: "bold" },
    );

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(10);

    currentY += 5;

    doc.text(stockData.transferfrom?.email || "-", fromX, currentY, {
      align: "center",
    });
    doc.text(stockData.transferto?.email || "-", toX, currentY, {
      align: "center",
    });

    const tableStartY = currentY + 10;
    const headRow = [
      "S NO",
      "Title",
      "MRP",
      "Discount",
      "Sale Price",
      ...(hasLandingCost ? ["Landing Cost"] : []),
      "Quantity",
      "Total",
      "Status",
    ];
    autoTable(doc, {
      startY: tableStartY,
      head: [headRow],
      body: (stockData.transferitems || []).map((product, index) => {
        const row = [
          { content: index + 1, styles: { halign: "center" } },
          { content: product.title || "-", styles: { halign: "center" } },
          {
            content: `₹${CurrencyConverter.toCurrency(
              product.mrp || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          {
            content: `₹${CurrencyConverter.toCurrency(
              product.discount || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          {
            content: `₹${CurrencyConverter.toCurrency(
              product.saleprice || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
        ];

        // Add Landing Cost column only if hasLandingCost is true
        if (hasLandingCost) {
          row.push({
            content: `₹ ${product.landingcost || 0}`,
            styles: { halign: "center" },
          });
        }

        // Remaining columns
        row.push(
          {
            content:
              product?.isSecondaryUnit === true
                ? `${toSecondaryQuantity(
                    product.quantity || 0,
                    product?.unitConfig || {},
                  )} ${product?.unitConfig?.secondaryUnitCode || ""}`
                : `${product.quantity || 0} ${product?.unitConfig?.primaryUnitCode || ""}`,
            styles: { halign: "center" },
          },
          {
            content: `₹${CurrencyConverter.toCurrency(
              product.lineGrandTotal || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          {
            content: product.status || "-",
            styles: {
              font: "Inter-Bold",
              fontStyle: "bold",
              textColor: getTextColor(product.status),
              halign: "center",
            },
          },
        );

        return row;
      }),
      margin: { left: 6 },
      theme: "grid",
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 10,
        lineColor: [230, 230, 230],
        lineWidth: 0.5,
        cellPadding: 2,
        valign: "middle",
      },
      headStyles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fillColor: [245, 245, 245],
        textColor: [33, 37, 41],
        halign: "center",
      },
      columnStyles: hasLandingCost
        ? {
            0: { cellWidth: 10 },
            1: { cellWidth: 40 },
            2: { cellWidth: 18 },
            3: { cellWidth: 18 },
            4: { cellWidth: 22 },
            5: { cellWidth: 20 },
            6: { cellWidth: 18 },
            7: { cellWidth: 22 },
            8: { cellWidth: 25, halign: "center" },
          }
        : {
            0: { cellWidth: 10 },
            1: { cellWidth: 45 },
            2: { cellWidth: 20 },
            3: { cellWidth: 20 },
            4: { cellWidth: 25 },
            5: { cellWidth: 25 },
            6: { cellWidth: 25, halign: "center" },
          },
    });

    // Summary Table
    const tableEndY = doc.lastAutoTable.finalY;
    const summaryTableHeight = 10;
    let summaryStartY = tableEndY;

    if (summaryStartY + summaryTableHeight > pageHeight - padding) {
      doc.addPage();
      summaryStartY = padding;
    }
    autoTable(doc, {
      startY: summaryStartY,
      showHead: "never",
      margin: { left: 6 },
      body: [
        [
          { content: "", colSpan: 1, styles: { halign: "center" } },
          { content: "Total", styles: { halign: "center" } },
          {
            content: `₹${CurrencyConverter.toCurrency(
              stockData.totalmrp || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          {
            content: `₹${CurrencyConverter.toCurrency(
              stockData.totaldiscount || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          {
            content: `₹${CurrencyConverter.toCurrency(
              stockData.totalsaleprice || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          ...(hasLandingCost
            ? [
                {
                  content: `₹${CurrencyConverter.toCurrency(
                    stockData.totallandingcost || 0,
                    "INR",
                  )}`,
                  styles: { halign: "center" },
                },
              ]
            : []),
          {
            content: `${stockData.totalqty || 0}`,
            styles: { halign: "center" },
          },
          {
            content: `₹${CurrencyConverter.toCurrency(
              stockData.grandtotal || 0,
              "INR",
            )}`,
            styles: { halign: "center" },
          },
          { content: "", styles: { halign: "center" } },
        ],
      ],
      styles: {
        font: "Inter-Bold",
        fontStyle: "bold",
        fontSize: 9,
        fillColor: [245, 245, 245],
        textColor: [33, 37, 41],
        cellPadding: 2,
        lineColor: [230, 230, 230],
        lineWidth: 0.5,
        halign: "center",
      },
      columnStyles: hasLandingCost
        ? {
            0: { cellWidth: 10, halign: "center" },
            1: { cellWidth: 40, halign: "center" },
            2: { cellWidth: 18, halign: "center" },
            3: { cellWidth: 18, halign: "center" },
            4: { cellWidth: 22, halign: "center" },
            5: { cellWidth: 20, halign: "center" },
            6: { cellWidth: 18, halign: "center" },
            7: { cellWidth: 22, halign: "center" },
            8: { cellWidth: 25, halign: "center" },
          }
        : {
            0: { cellWidth: 10, halign: "center" },
            1: { cellWidth: 45, halign: "center" },
            2: { cellWidth: 20, halign: "center" },
            3: { cellWidth: 20, halign: "center" },
            4: { cellWidth: 25, halign: "center" },
            5: { cellWidth: 25, halign: "center" },
            6: { cellWidth: 25, halign: "center" },
          },
    });
    return doc;
  };

  useEffect(() => {
    jsPDF.API.events.push([
      "addFonts",
      function () {
        this.addFileToVFS("Inter_24pt-Regular-normal.ttf", InterRegular);
        this.addFileToVFS("Inter_24pt-Bold-normal.ttf", InterBold);
        this.addFont(
          "Inter_24pt-Regular-normal.ttf",
          "Inter-Regular",
          "normal",
        );
        this.addFont("Inter_24pt-Bold-normal.ttf", "Inter-Bold", "bold");
      },
    ]);
  }, []);

  const handlePrint = async () => {
    const doc = await generateTransferPDF();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }, 500);
    };
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-label-warning";
      case "accepted":
        return "bg-label-green";
      default:
        return "bg-label-secondary";
    }
  };

  const displayPriceTotalMrp = stockData.transferitems.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    return total + displayPrices.lineTotalMrp ;
  }, 0);

  const displayPriceTotalDisc = stockData.transferitems.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
    return total + displayPrices.lineTotalDisc;
  }, 0);
  
  const displayPriceTotalSalePrice = stockData.transferitems.reduce((total, item) => {
    const displayPrices = getDisplayPrices(item);
  
    return total+ displayPrices.lineTotalSalePrice;
  }, 0);
  // const handleUpdate = async (citem, index) => {
  //   try {
  //     if (!checkNetwork()) return;
  //     setEditBtnLoading((prev) => ({ ...prev, [index]: true }));
  //     const transferId = stockData._id;
  //     const sendData = {
  //       transferitemid: citem._id,
  //       quantity: citem.quantity,
  //       landingcost: citem.landingcost || 0,
  //     };
  //     const response = await AxiosInstance.patch(
  //       `module/stocktransfer/${transferId}`,
  //       sendData,
  //     );
  //     showAlert("success", Messages.SUCCESS.STOCK_UPDATED);
  //     const updatedStock = response.data.results;
  //     setStockData(updatedStock);
  //     onStockUpdate({ updatedStock, transferId: stockData._id });
  //   } catch (error) {
  //     handleError(error, showAlert);
  //   } finally {
  //     setEditBtnLoading((prev) => ({ ...prev, [index]: false }));
  //   }
  // };

  const handleUpdate = async (citem, index) => {
    try {
      if (!checkNetwork()) return;

      setEditBtnLoading((prev) => ({ ...prev, [index]: true }));

      const transferId = stockData._id;

      let quantity = citem.quantity;

      // // Convert displayed secondary qty back to primary qty for API
      // if (citem?.isSecondaryUnit === true) {
      //   const converted = toPrimaryQuantity(
      //     citem.quantity,
      //     citem?.unitConfig || {},
      //   );

      //   quantity = converted.primaryQty;
      // }

      const sendData = {
        transferitemid: citem._id,
        quantity,
        landingcost: citem.landingcost || 0,
        isSecondaryUnit: citem?.isSecondaryUnit, // send secondary unit flag to backend
      };

      const response = await AxiosInstance.patch(
        `module/stocktransfer/${transferId}`,
        sendData,
      );

      showAlert("success", Messages.SUCCESS.STOCK_UPDATED);

      const updatedStock = response.data.results;

      setStockData(updatedStock);

      onStockUpdate({
        updatedStock,
        transferId: stockData._id,
      });
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setEditBtnLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleDelete = async (citem, index) => {
    try {
      if (!checkNetwork()) return;
      setDeleteBtnLoading((prev) => ({ ...prev, [index]: true }));

      const transferid = stockData._id;
      const transferitemid = citem._id;

      const response = await AxiosInstance.delete(
        `module/stocktransfer/${transferid}/item/${transferitemid}`,
      );
      showAlert("success", Messages.SUCCESS.STOCK_DELETED);

      const updatedStock = response.data.results;
      if (updatedStock) {
        setStockData(updatedStock);
        onStockUpdate({ updatedStock, transferid: stockData._id });
      } else {
        onClose();
        onStockUpdate({ updatedStock: null, transferid: stockData._id });
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setDeleteBtnLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const getText = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "Pending";
      case "ACCEPTED":
        return "Accepted";
      case "RECEIVED":
        return "Pending";
      default:
        return status;
    }
  };

  const handleClickBarcode = (code) => {
    if (
      hasPermission("PRODUCT", "PRODUCT:viewByCode") ||
      permission === undefined
    ) {
      setSelectedBarcode(code); // set the selected barcode
      setShowBarcodeModal(true); // show the modal
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        id="StockTrasferModal"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">STOCK TRANSFER</h6>
              <button
                type="button"
                onClick={handleClose}
                className="btn-close border"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body" id="printable-content">             
     
              <div className="pb-2 border-0">
                <div className="card-body p-0">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      {isUser.profileData?.image && (
                        <img
                          src={uRLs.IMAGE_URL + "profile/" + isUser.profileData.image}
                          alt="logo"
                          style={{
                            maxWidth: "120px",
                            maxHeight: "80px",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </div>

                  <div className="col-md-2">
                      <small className="text-muted d-block">Transfer No</small>
                      <strong>{stockData.transferno}</strong>
                    </div>

                    <div className="col-md-2">
                      <small className="text-muted d-block">Transfer Date</small>
                      <strong>
                      {stockData?.transferdate
                            ? DateConverter.ISOToDisplayFormat(
                                stockData.transferdate
                              ).date
                            : "N/A"}
                      </strong>
                    </div>
                  </div>
                
                <div className="position-relative text-center my-2">
                  <div className="position-absolute top-50 start-0 end-0 border-top translate-middle-y z-0"></div>
                  <span className="position-relative bg-white px-4 text-secondary fw-bold tracking-widest small z-1">
                    TRANSFER RECEIPT
                  </span>
                </div>
                
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div
                        className="p-2 h-100"
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "10px",
                          background: "#f8f9fa",
                        }}
                      >
                        <h6 className="fw-bold text-primary mb-2 font-14">
                          From
                        </h6>

                        <h6 className="mb-1 font-12" >
                          {stockData.transferfrom?.stype} -{" "}
                          {stockData.transferfrom?.title}
                        </h6>

                        <p className="mb-0 text-muted font-12">
                          {stockData.transferfrom?.email}
                        </p>
                      </div>
                    </div>
                <div className="col-md-1 text-center" style={{ marginTop: "2.5rem"}}>
                  <div className="rounded-circle"
                    style={{
                      width: "50px",
                      height: "50px",
                      margin: "auto",        
                      background: "#e8f0fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                    className="fadeIn animated bx bx-transfer-alt"
                      style={{
                        fontSize: "20px",
                        color: "#0d6efd",
                      }}
                    />
                  </div>
                </div>
                    <div className="col-md-4">
                      <div
                        className="p-2 h-100"
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "10px",
                          background: "#f8f9fa",
                        }}
                      >
                        <h6 className="fw-bold text-success mb-2 font-14">
                          To
                        </h6>

                        <h6 className="mb-1 font-12">
                          {stockData.transferto?.stype} -{" "}
                          {stockData.transferto?.title}
                        </h6>

                        <p className="mb-0 text-muted font-12">
                          {stockData.transferto?.email}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-3"> <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total Mrp :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                        
                        {formatCurrencyFunWithoutConvert(displayPriceTotalMrp)}
                      </h6>
                    </div>
                  <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total Discount :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                        
                        {formatCurrencyFunWithoutConvert(displayPriceTotalDisc)}
                      </h6>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0" style={{ fontSize: "13px" }}>
                        Total salePrice :
                      </p>
                      <h6 className="mb-0 fs-20" style={{ fontSize: "14px" }}>
                       
                        {formatCurrencyFunWithoutConvert(displayPriceTotalSalePrice)}
                      </h6>
                    </div>
                    <div className="bg-light rounded p-2 text-center">
                      <h6 className="mb-0 fs-18 text-dark">
                        Grand Total :{" "}
                        <span>
                          {formatCurrencyFun(stockData?.grandtotal)}
                        </span>
                      </h6>
                    </div></div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12 table-responsive rtable">
                <div className="stock-table-scroll">
                  <table className="table align-middle table-sm table-bordered mb-0 rtable">
                    <thead className="bg-light">
                      <tr className="text-center">
                        <th>S No</th>
                        <th>Title</th>
                        <th>MRP</th>
                        <th>Discount</th>
                        <th>Sale Price</th>
                        {hasLandingCost && <th>Landing Cost</th>}
                        <th>Qty</th>
                        <th>Total</th>
                        <th className="w-15">Status</th>
                        {/* <th>Actions</th> */}
                      </tr>
                    </thead>
                    <tbody className="text-center">
                      {stockData.transferitems.map((product, index) => {
                        const displayPrices = getDisplayPrices(product);
                        const displayUnit = product?.unitConfig
                          ? product?.isSecondaryUnit
                            ? product?.unitConfig?.secondaryUnitCode
                            : product?.unitConfig?.primaryUnitCode
                          : "Unit";
                      return (
                        <tr
                          key={index}
                          onClick={
                            product.status === "PENDING"
                              ? () => openEditModal(product, index)
                              : product.status === "ACCEPTED" ?
                              ()=>handleClickBarcode(product)
                              : undefined
                          }
                          className="position-relative  cursor-poInter hover-row"
                        >
                          <td>{index + 1}</td>
                          <td className="text-wrap text-start">{product.title}</td>
                          <td className="text-end">
                            {formatCurrencyFunWithoutConvert(displayPrices.mrp)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyFunWithoutConvert(displayPrices.discount)}
                          </td>
                          <td className="text-end p-3">
                            {formatCurrencyFunWithoutConvert(displayPrices.saleprice)}
                          </td>
                          {hasLandingCost && (
                            <td className="text-end">
                              {formatCurrencyFun(product.landingcost)}
                            </td>
                          )}

                          <td className="text-center">
                            {product?.unitConfig
                              ? product?.isSecondaryUnit === true
                                ? (() => {
                                    const secondaryData = toSecondaryQuantity(
                                      product.quantity || 0,
                                      product.unitConfig,
                                    );

                                    return `${secondaryData?.secondaryQty || 0} ${
                                      secondaryData?.unit || ""
                                    }`;
                                  })()
                                : `${product.quantity || 0} ${
                                    product?.unitConfig?.primaryUnitCode || ""
                                  }`
                              : product?.quantity || 0}
                          </td>

                          <td className="text-end">
                            {formatCurrencyFun(product.lineGrandTotal)}
                          </td>
                          <td className="no-print position-relative">
                          <div className=" d-flex justify-content-center align-items-center">
                            <span
                              className={`stock-status-badge badge rounded-pill ${getColor(
                                product.status,
                              )}`}
                            >
                              {getText(product.status)}
                            </span>
                           
                            <div className="order-actions order-actions3 opacity-0 ">
                              {product.status === "PENDING" && (
                                <>
                                  <button
                                    type="button"
                                    className="btn-label-teal shadow-none me-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditModal(product, index);
                                    }}
                                  >
                                    <EditIcon/>
                                  </button>

                                  <button
                                    type="button"
                                    className="btn-label-primary shadow-none me-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClickBarcode(product);
                                    }}
                                  >
                                    <BarcodeIcon/>
                                  </button>
                                  {isDeleteBtnLoading[index] ? (
                                    <button
                                      className="btn-label-danger shadow-none disabled"
                                      disabled
                                    >
                                      <CircularProgress
                                        size={18}
                                        className="text-danger"
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-label-danger shadow-none"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(product);
                                      }}
                                    >
                                      <TrashIcon/>
                                    </button>
                                  )}
                                </> )}
                                {product.status==="ACCEPTED" &&(
                                  <button
                                  type="button"
                                  className="btn-label-primary shadow-none me-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickBarcode(product);
                                  }}
                                >
                                  <BarcodeIcon/>
                                </button>
                                )}
                            </div>
                            
                            </div>
                          </td>
                          {/* <td className="text-center no-print">
                            <div className="order-actions opacity-0 opacity-md-100">
                              {product.status === "PENDING" && (
                                <>
                                  <button
                                    type="button"
                                    className="btn-label-teal shadow-none me-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditModal(product, index);
                                    }}
                                  >
                                    <i className="ng ng-ng-edit-icon"></i>
                                  </button>

                                  {isDeleteBtnLoading[index] ? (
                                    <button
                                      className="btn-label-danger shadow-none disabled"
                                      disabled
                                    >
                                      <CircularProgress
                                        size={18}
                                        className="text-danger"
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn-label-danger shadow-none"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(product);
                                      }}
                                    >
                                      <i className="bx bx-trash"></i>
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td> */}
                        </tr>
                      )})}
                      {/* <tr>
                        <td colSpan="1"></td>
                        <td className="bg-light">
                          <h6 className="mb-0">Total</h6>
                        </td>
                        <td className="bg-light">
                          <h6 className="mb-0">
                            ₹
                            {CurrencyConverter.toCurrency(
                              stockData.totalmrp || 0,
                              "INR",
                            )}
                          </h6>
                        </td>
                        <td className="bg-light">
                          <h6 className="mb-0">
                            ₹
                            {CurrencyConverter.toCurrency(
                              stockData.totaldiscount || 0,
                              "INR",
                            )}
                          </h6>
                        </td>
                        <td className="bg-light">
                          <h6 className="mb-0">
                            ₹
                            {CurrencyConverter.toCurrency(
                              stockData.totalsaleprice || 0,
                              "INR",
                            )}
                          </h6>
                        </td>

                       
                        {hasLandingCost && (
                          <td className="bg-light">
                            <h6 className="mb-0"></h6>{" "}
                          </td>
                        )}

                        <td className="bg-light">
                          <h6 className="mb-0"></h6>
                        </td>
                        <td className="bg-light">
                          <h6 className="mb-0">
                            ₹
                            {CurrencyConverter.toCurrency(
                              stockData.grandtotal || 0,
                              "INR",
                            )}
                          </h6>
                        </td>
                        <td></td>
                      </tr> */}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-block">
              <button
                onClick={handleClose}
                type="button"
                data-bs-dismiss="modal"
                className="btn btn-outline-secondary text-left"
              >
                <XIcon/> Close
              </button>
              <button
                onClick={handlePrint}
                type="button"
                className="btn btn-info text-right"
              >
                <PrinterIcon iClass={"fs-15"}/> Print
              </button>
            </div>
          </div>
          {editItem && (
            <MatchStock
              data={[editItem]} // make it array for table rendering
              citem={editItem} // keep as object, not array
              mode="update"
              onClose={closeEditModal}
              onUpdate={(updatedItem) => {
                handleUpdate(
                  updatedItem,
                  stockData.transferitems.findIndex(
                    (i) => i._id === updatedItem._id,
                  ),
                );
                closeEditModal();
              }}
            />
          )}
          {showBarcodeModal && uRLs.BARCODE_PRINT === "true" && (
                        <BarcodeGenerator
                          barcodeValue={selectedBarcode.code}
                          product={selectedBarcode}
                          onClose={() => setShowBarcodeModal(false)}
                        />
                      )}
        </div>
      </div>
    </div>
  );
};

export default ViewStockTransfer;
